// @ts-nocheck
'use client'

import { useState } from 'react'
import { createClient } from '@/lib/supabase/client'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { InstrumentedButton } from '@/components/ui/instrumented-button'
import { useInstrumentedAction } from '@/hooks/useInstrumentedAction'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { BookOpen, Loader2, CheckCircle, Mail, RefreshCw } from 'lucide-react'
import { AppErrorBoundary } from '@/components/common/AppErrorBoundary'

export default function SignUpPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [resendMessage, setResendMessage] = useState('')
  const router = useRouter()
  const supabase = createClient()

  const handleResendConfirmation = async () => {
    setResendLoading(true)
    setResendMessage('')
    setError('')

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: process.env['NODE_ENV'] === 'production' 
            ? `${process.env['NEXT_PUBLIC_SITE_URL'] || window.location.origin}/my-library`
            : `${window.location.origin}/my-library`
        }
      })

      if (error) {
        console.error('[Resend] Error:', error)
        
        // Handle specific error cases
        if (error.message.includes('rate limit')) {
          setError('Please wait a moment before requesting another email.')
        } else if (error.message.includes('already confirmed')) {
          setError('This email address has already been confirmed. Please try signing in.')
        } else if (error.message.includes('invalid email')) {
          setError('Please enter a valid email address.')
        } else if (error.message.includes('too many requests')) {
          setError('Too many requests. Please wait a few minutes before trying again.')
        } else {
          setError(`Unable to resend email: ${error.message}`)
        }
      } else {
        setResendMessage('Confirmation email sent! Please check your inbox and spam folder.')
      }
    } catch (err) {
      console.error('[Resend] Exception:', err)
      
      // Handle network or unexpected errors
      if (err instanceof Error) {
        if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
          setError('Network error. Please check your connection and try again.')
        } else {
          setError('An unexpected error occurred. Please try again later.')
        }
      } else {
        setError('Failed to resend confirmation email. Please try again.')
      }
    } finally {
      setResendLoading(false)
    }
  }

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')
    setResendMessage('')

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      setLoading(false)
      return
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters')
      setLoading(false)
      return
    }

    try {
      console.log('[Sign Up] Attempting email/password sign up...')
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName
          },
          emailRedirectTo: process.env['NODE_ENV'] === 'production' 
            ? `${process.env['NEXT_PUBLIC_SITE_URL'] || window.location.origin}/my-library`
            : `${window.location.origin}/my-library`
        }
      })

      if (error) {
        console.error('[Sign Up] Error:', error)
        
        // Handle specific signup error cases
        if (error.message.includes('already registered')) {
          setError('An account with this email already exists. Please sign in or use a different email address.')
        } else if (error.message.includes('invalid email')) {
          setError('Please enter a valid email address.')
        } else if (error.message.includes('password')) {
          setError('Password must be at least 6 characters long.')
        } else if (error.message.includes('rate limit')) {
          setError('Too many signup attempts. Please wait a few minutes before trying again.')
        } else if (error.message.includes('email not allowed')) {
          setError('This email domain is not allowed. Please use a different email address.')
        } else if (error.message.includes('signup disabled')) {
          setError('Account creation is currently disabled. Please contact support.')
        } else {
          setError(`Signup failed: ${error.message}`)
        }
        setLoading(false)
        return
      }

      console.log('[Sign Up] Success! User:', data.user?.email)

      // Create profile using API endpoint - but don't fail the signup if this fails
      if (data.user) {
        console.log('[Sign Up] Creating user profile via API...')
        try {
          const profileResponse = await fetch('/api/profiles/create', {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              fullName: fullName
            })
          })

          if (!profileResponse.ok) {
            const errorData = await profileResponse.json()
            console.error('[Sign Up] Profile creation failed:', errorData.error)
            // Don't fail the signup - profile can be created later
          } else {
            const profileData = await profileResponse.json()
            console.log('[Sign Up] Profile created successfully via API:', profileData.created ? 'NEW' : 'EXISTING')
          }
        } catch (profileError) {
          console.error('[Sign Up] Profile creation failed:', profileError)
          // Don't fail the signup - profile can be created later
        }
      }

      // Handle email confirmation flow
      if (data.session) {
        // Email confirmation is disabled - user is immediately signed in
        console.log('[Sign Up] Email confirmation disabled - redirecting to library')
        router.push('/my-library')
      } else {
        // Email confirmation is required
        console.log('[Sign Up] Email confirmation required - showing confirmation screen')
        setShowConfirmation(true)
        setMessage('Account created successfully!')
      }
    } catch (err) {
      console.error('[Sign Up] Exception:', err)
      
      // Handle network or unexpected errors
      if (err instanceof Error) {
        if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
          setError('Network error. Please check your internet connection and try again.')
        } else if (err.message.includes('timeout')) {
          setError('Request timed out. Please try again.')
        } else {
          setError('An unexpected error occurred. Please try again later.')
        }
      } else {
        setError('An unexpected error occurred. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSignIn = () => {
    router.push('/auth/signin')
  }

  // Instrumented handlers
  const instrumentedSignUp = useInstrumentedAction(
    handleSignUp,
    { eventName: 'auth.signup.submit', component: 'signup-page', errorMessage: 'Failed to create account' }
  );

  const instrumentedResendConfirmation = useInstrumentedAction(
    handleResendConfirmation,
    { eventName: 'auth.signup.resend_email', component: 'signup-page', errorMessage: 'Failed to resend email' }
  );

  const instrumentedBackToSignup = useInstrumentedAction(
    () => setShowConfirmation(false),
    { eventName: 'auth.signup.back', component: 'signup-page', errorMessage: '' }
  );

  const instrumentedNavigateSignin = useInstrumentedAction(
    handleSignIn,
    { eventName: 'auth.navigation.signin', component: 'signup-page', errorMessage: '' }
  );

  // Show confirmation screen after successful signup
  if (showConfirmation) {
    return (
      <AppErrorBoundary source="auth-signup-confirmation">
        {/* Monitoring boundary ensures ErrorBoundary coverage from '@/components/common/ErrorMonitoringV2' */}
        <div className="min-h-screen flex items-center justify-center bg-theme-bg-primary p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-8 h-8 accent-bg-primary rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-[var(--color-text-on-accent)]" />
              </div>
              <span className="text-xl font-bold">Infinite-Pages</span>
            </div>
            <CardTitle className="text-2xl">Check your email</CardTitle>
            <CardDescription>
              We&apos;ve sent a confirmation link to your email address
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-theme-bg-success rounded-full flex items-center justify-center mx-auto">
                <Mail className="w-8 h-8 text-theme-text-success" />
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  We sent a confirmation link to:
                </p>
                <p className="font-medium text-lg">{email}</p>
              </div>

              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  Click the link in the email to confirm your account and start creating amazing stories.
                  <br />
                  <strong>Don&apos;t see the email?</strong> Check your spam folder or wait a few minutes for delivery.
                </AlertDescription>
              </Alert>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {resendMessage && (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>{resendMessage}</AlertDescription>
                </Alert>
              )}
            </div>

            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-3">
                  Didn&apos;t receive the email?
                </p>
                <InstrumentedButton
                  eventName="auth.signup.resend_email"
                  component="signup-page"
                  errorMessage="Failed to resend email"
                  onClick={instrumentedResendConfirmation}
                  disabled={resendLoading}
                  variant="outline"
                  className="w-full"
                >
                  {resendLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Resend confirmation email
                    </>
                  )}
                </InstrumentedButton>
              </div>

              <div className="text-center">
                <InstrumentedButton
                  eventName="auth.signup.back"
                  component="signup-page"
                  errorMessage=""
                  onClick={instrumentedBackToSignup}
                  variant="ghost"
                  className="w-full"
                >
                  Back to signup
                </InstrumentedButton>
              </div>
            </div>

            <div className="text-center text-sm">
              <span className="text-muted-foreground">Already confirmed? </span>
              <InstrumentedButton
                type="button"
                variant="link"
                className="p-0 h-auto"
                eventName="auth.navigation.signin"
                component="signup-page"
                errorMessage=""
                onClick={instrumentedNavigateSignin}
              >
                Sign in
              </InstrumentedButton>
            </div>
          </CardContent>
        </Card>
        </div>
      </AppErrorBoundary>
    )
  }

  return (
    <AppErrorBoundary source="auth-signup-page">
      {/* Monitoring boundary ensures ErrorBoundary coverage from '@/components/common/ErrorMonitoringV2' */}
      <div className="min-h-screen flex items-center justify-center bg-theme-bg-primary p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-8 accent-bg-primary rounded-lg flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-[var(--color-text-on-accent)]" />
            </div>
            <span className="text-xl font-bold">Infinite-Pages</span>
          </div>
          <CardTitle className="text-2xl">Create your account</CardTitle>
          <CardDescription>
            Join thousands of writers creating amazing stories with AI
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {message && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={instrumentedSignUp} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name</Label>
              <Input
                id="fullName"
                name="fullName"
                type="text"
                autoComplete="name"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground">
                Must be at least 6 characters
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create account'
              )}
            </Button>
          </form>

          <div className="text-center text-sm">
            <span className="text-muted-foreground">Already have an account? </span>
            <Button
              type="button"
              variant="link"
              className="p-0 h-auto"
              onClick={handleSignIn}
            >
              Sign in
            </Button>
          </div>

          <div className="text-xs text-muted-foreground text-center">
            By creating an account, you agree to our{' '}
            <button type="button" className="underline hover:text-foreground bg-transparent border-0 p-0 cursor-pointer">
              Terms of Service
            </button>{' '}
            and{' '}
            <button type="button" className="underline hover:text-foreground bg-transparent border-0 p-0 cursor-pointer">
              Privacy Policy
            </button>
          </div>
        </CardContent>
      </Card>
      </div>
    </AppErrorBoundary>
  )
}